import type { Identifier } from "ra-core";

import type {
  AdvisorAvailabilityBlock,
  LeadAssignmentEligibleAdvisor,
  Sale,
  SalesProject,
  TeamRosterEntry,
} from "../types";

/**
 * Almikia Epic EL (TASK-008): pure helpers of the Direccion "Asignacion de leads" screen.
 * Deliberately no ranking, counts of received leads or comparison between advisors.
 */

/** Why an advisor cannot be switched on in a project's rotation. */
export type UnavailableReason = "disabled" | "departed";

export type SelectableAdvisor = {
  salesId: Identifier;
  name: string;
  /** The advisor's "clave"; empty when she has none yet. */
  code: string;
  isEligible: boolean;
  /** Set when she cannot receive leads: the toggle must be disabled and cannot be on. */
  unavailable: UnavailableReason | null;
  /** Neutral indicator only: she has chosen at least one active availability block. */
  hasChosenBlocks: boolean;
};

type DeriveInput = {
  projectId: Identifier;
  sales: Pick<
    Sale,
    "id" | "first_name" | "last_name" | "disabled" | "advisor_code"
  >[];
  memberships: Pick<SalesProject, "sales_id" | "project_id" | "ended_at">[];
  eligible: Pick<
    LeadAssignmentEligibleAdvisor,
    "project_id" | "sales_id" | "is_eligible"
  >[];
  roster: Pick<TeamRosterEntry, "sales_id" | "departed_at">[];
  blocks: Pick<AdvisorAvailabilityBlock, "sales_id" | "ended_at">[];
};

const sameId = (a: Identifier | null | undefined, b: Identifier) =>
  a != null && String(a) === String(b);

/**
 * Advisors Direccion can see for a project: only people with an ACTIVE membership in it
 * (non-members are excluded). Disabled or departed members are kept but flagged
 * `unavailable` and always reported as not eligible, so the UI shows them switched off and
 * non-toggleable. Sorted by name for a stable, non-comparative order.
 */
export const deriveSelectableAdvisors = ({
  projectId,
  sales,
  memberships,
  eligible,
  roster,
  blocks,
}: DeriveInput): SelectableAdvisor[] => {
  const memberIds = new Set(
    memberships
      .filter((m) => sameId(m.project_id, projectId) && !m.ended_at)
      .map((m) => String(m.sales_id)),
  );
  return sales
    .filter((sale) => memberIds.has(String(sale.id)))
    .map((sale): SelectableAdvisor => {
      const departed = roster.some(
        (entry) => sameId(entry.sales_id, sale.id) && !!entry.departed_at,
      );
      const unavailable: UnavailableReason | null = departed
        ? "departed"
        : sale.disabled
          ? "disabled"
          : null;
      const flagged = eligible.some(
        (row) =>
          sameId(row.project_id, projectId) &&
          sameId(row.sales_id, sale.id) &&
          row.is_eligible,
      );
      return {
        salesId: sale.id,
        name: `${sale.first_name} ${sale.last_name}`.trim(),
        code: sale.advisor_code ?? "",
        isEligible: unavailable === null && flagged,
        unavailable,
        hasChosenBlocks: blocks.some(
          (block) => sameId(block.sales_id, sale.id) && !block.ended_at,
        ),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
};

/** Lower-cases and strips accents/non-alphanumerics into a channel code candidate. */
export const slugifyChannelCode = (label: string): string =>
  label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export type ChannelInputError =
  | "label_required"
  | "code_required"
  | "duplicate";

/**
 * Validates a new channel (label + code). The code is normalised, never checked against a
 * fixed list of values: any new code is valid as long as it is not already in the catalogue.
 */
export const validateNewChannel = (
  label: string,
  code: string,
  existingCodes: string[],
): { error: ChannelInputError | null; label: string; code: string } => {
  const cleanLabel = label.trim();
  const cleanCode = slugifyChannelCode(code);
  if (!cleanLabel)
    return { error: "label_required", label: cleanLabel, code: cleanCode };
  if (!cleanCode)
    return { error: "code_required", label: cleanLabel, code: cleanCode };
  if (existingCodes.includes(cleanCode)) {
    return { error: "duplicate", label: cleanLabel, code: cleanCode };
  }
  return { error: null, label: cleanLabel, code: cleanCode };
};
